import { inject, Injectable, signal } from '@angular/core';
import { UtilsService, CoreService } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { AuditEvent, AuditLog, AuditLogDetail, AuditResponse } from '../models/audit.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationAuditService {

    // ================= SIGNALS =================
    private _audits = signal<AuditEvent[]>([]);
    public audits = this._audits.asReadonly();

    private _total = signal<number>(0);
    public total = this._total.asReadonly();

    private _isLoading = signal<boolean>(false);
    public isLoading = this._isLoading.asReadonly();

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);

    private identityBaseUrl = environment.identityUrl;

    // ================= FILTER OPTIONS =================
    private _actorOptions = signal<any[]>([]);
    readonly actorOptions = this._actorOptions.asReadonly();

    private _eventOptions = signal<any[]>([]);
    readonly eventOptions = this._eventOptions.asReadonly();

    private _dateOptions = signal<any[]>([]);
    readonly dateOptions = this._dateOptions.asReadonly();

    // ================= SELECTED FILTERS =================
    private _selectedActor = signal<string | null>(null);
    readonly selectedActor = this._selectedActor.asReadonly();

    private _selectedEvent = signal<string | null>(null);
    readonly selectedEvent = this._selectedEvent.asReadonly();

    private _selectedDate = signal<string | null>(null);
    readonly selectedDate = this._selectedDate.asReadonly();
    private _loadedOrgId = signal<number | null>(null);
    private _lastQueryKey: string | null = null;

    constructor() {
        this.initFilters();
    }

    // ================= INIT =================
    private initFilters() {
        this._actorOptions.set([
            { label: 'Actor: All', value: 'all' }
        ]);

        this._eventOptions.set([
            { label: 'Event: All', value: 'all' }
        ]);

        this._dateOptions.set([
            { label: 'Last 7 days', value: '7d' },
            { label: 'Last 14 days', value: '14d' },
            { label: 'Last 20 days', value: '20d' },
            { label: 'Last 30 days', value: '30d' }
        ]);

        // ✅ default selections
        this._selectedActor.set('all');
        this._selectedEvent.set('all');
        this._selectedDate.set('30d');
    }

    // ================= SETTERS =================
    setActor(value: string) {
        this._selectedActor.set(value);
        this._lastQueryKey = null;
    }

    setEvent(value: string) {
        this._selectedEvent.set(value);
        this._lastQueryKey = null;
    }

    setDate(value: string) {
        this._selectedDate.set(value);
        this._lastQueryKey = null;
    }

    // ================= LOAD =================
    public async loadAuditLogsIfNeeded(
        orgId: number,
        page: number = 1,
        pageSize: number = 25,
        force: boolean = false
    ): Promise<void> {

        const params = this.buildParams(page, pageSize);
        const currentLoadedOrgId = this._loadedOrgId();
        const hasAuditsForOrg = currentLoadedOrgId === orgId;

        // 👉 create unique key based on params
        const queryKey = JSON.stringify(params);

        // 👉 skip if same request
        if (!force && hasAuditsForOrg && this._lastQueryKey === queryKey && this._audits().length > 0) {
            return;
        }

        this._lastQueryKey = queryKey;
        this._loadedOrgId.set(orgId);

        this._audits.set([]); // clear current audits while loading new ones
        this._total.set(0); // reset total count

        const res = await this.fetchAuditLogs(orgId, params);

        if (res?.items) {
            this._audits.set(res.items.map(item => this.mapToAuditEvent(item)));
            this._total.set(res.total);

            // push the available actors and events into the filter options (if not already present)
            const uniqueActors = Array.from(new Set(res.items.map(item => item.actorName))).map(name => ({ label: name, value: name }));
            const uniqueEvents = Array.from(new Set(res.items.map(item => item.action))).map(action => ({ label: action, value: action }));
            this._actorOptions.set([{ label: 'Actor: All', value: 'all' }, ...uniqueActors]);
            this._eventOptions.set([{ label: 'Event: All', value: 'all' }, ...uniqueEvents]);

        }
    }

        // ================= FILTER PARAM BUILDER =================
    private buildParams(page: number, pageSize: number) {
        let from: string | undefined;

        if (this._selectedDate() === '7d') {
            from = this.getDateDaysAgo(7);
        } else if (this._selectedDate() === '30d') {
            from = this.getDateDaysAgo(30);
        }

        return {
            page,
            pageSize,
            actor: this._selectedActor() !== 'all' ? this._selectedActor() : undefined,
            action: this._selectedEvent() !== 'all' ? this._selectedEvent() : undefined,
            from
        };
    }

    private getDateDaysAgo(days: number): string {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString();
    }

    // ================= MAP API → UI =================
    private mapToAuditEvent(item: AuditLog): AuditEvent {
        return {
            id: item.id,
            createdDate: this.formatDate(item.createdDate),
            actorUserId: item.actorUserId,
            actorName: item.actorName,
            action: item.action,
            summary: item.summary,
            targetType: item.targetType,
            targetId: item.targetId
        };
    }

    private formatDate(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleString();
    }

    // ================= FETCH LIST =================
    public async fetchAuditLogs(
        orgId: number,
        params?: {
            from?: string;
            to?: string;
            action?: string;
            actor?: string;
            q?: string;
            page?: number;
            pageSize?: number;
        }
    ): Promise<AuditResponse | null> {

        this.utils.logger.info('fetchAuditLogs() orgId:', orgId);

        if (!orgId) {
            this.utils.logger.warn('fetchAuditLogs() No orgId provided');
            return null;
        }

        this._isLoading.set(true);

        try {
            let url = `${this.identityBaseUrl}/organizations/${orgId}/audit`;

            const queryParams: string[] = [];

            if (params) {
                if (params.from) queryParams.push(`from=${params.from}`);
                if (params.to) queryParams.push(`to=${params.to}`);
                if (params.action) queryParams.push(`action=${params.action}`);
                if (params.actor) queryParams.push(`actor=${params.actor}`);
                if (params.q) queryParams.push(`q=${params.q}`);
                if (params.page) queryParams.push(`page=${params.page}`);
                if (params.pageSize) queryParams.push(`pageSize=${params.pageSize}`);
            }

            // const paramsNew = this.buildParams(params?.page || 1, params?.pageSize || 25);

            if (queryParams.length > 0) {
                url += `?${queryParams.join('&')}`;
            }

            this.utils.logger.info('fetchAuditLogs() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            this.utils.logger.info('fetchAuditLogs() response:', data);

            return data as AuditResponse;

        } catch (error) {
            this.utils.logger.error('fetchAuditLogs() error:', error);
            return null;

        } finally {
            this._isLoading.set(false);
            this.utils.logger.info('fetchAuditLogs() completed');
        }
    }

    // ================= FETCH SINGLE =================
    public async fetchAuditById(orgId: number, auditId: string): Promise<AuditLogDetail | null> {

        this.utils.logger.info('fetchAuditById()', { orgId, auditId });

        if (!orgId || !auditId) {
            this.utils.logger.warn('fetchAuditById() Missing params');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/organizations/${orgId}/audit/${auditId}`;
            this.utils.logger.info('fetchAuditById() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            return data as AuditLogDetail;

        } catch (error) {
            this.utils.logger.error('fetchAuditById() error:', error);
            return null;

        } finally {
            this.utils.logger.info('fetchAuditById() completed');
        }
    }

    // ================= CLEAR =================
    clearAudits(): void {
        this._audits.set([]);
        this._total.set(0);
    }
}
